import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, api } from "@/lib/api";
import { COST_DATA, money } from "@/lib/medicalData";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquareHeart, Send, Sparkles, ShieldAlert, ArrowRight, Loader2,
  Heart, Smile, Baby, Bone, Scissors, Activity, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const SCENARIOS = [
  { icon: Heart, text: "I need a heart surgery consultation" },
  { icon: Smile, text: "Looking for affordable dental work" },
  { icon: Baby, text: "Exploring fertility treatment options" },
  { icon: Bone, text: "I need orthopedic surgery (knee/hip)" },
  { icon: Scissors, text: "Interested in cosmetic procedures" },
  { icon: Activity, text: "I want to understand my cancer treatment options" },
];

const COST_KEYWORDS = [
  ["cardiac", ["heart", "cardiac", "bypass", "valve", "angioplast"]],
  ["dental", ["dental", "teeth", "tooth", "implant", "veneer"]],
  ["fertility", ["fertility", "ivf", "conceive", "egg freez"]],
  ["orthopedic", ["knee", "hip", "orthopedic", "joint", "spine", "spinal"]],
  ["cosmetic", ["cosmetic", "rhinoplast", "nose job", "liposuction", "facelift"]],
  ["oncology", ["cancer", "oncolog", "chemo", "tumor", "tumour"]],
  ["ophthalmology", ["lasik", "cataract", "vision", "eye surgery"]],
  ["bariatric", ["bariatric", "gastric", "weight loss", "obesity"]],
  ["transplant", ["transplant", "kidney", "liver"]],
  ["healthcheckup", ["checkup", "check-up", "full body", "screening", "preventive", "health check"]],
  ["hairtransplant", ["hair transplant", "hair loss", "balding", "fue"]],
  ["stemcell", ["stem cell", "regenerative"]],
  ["addictionrehab", ["addiction", "de-addiction", "rehab", "detox", "alcohol", "substance"]],
  ["strokerehab", ["stroke", "neuro rehab", "brain injury"]],
  ["panchakarma", ["ayurveda", "panchakarma", "yoga", "meditation"]],
  ["cardiacrehab", ["cardiac rehab", "heart rehab", "post-bypass"]],
];

function detectCost(text) {
  const t = text.toLowerCase();
  for (const [key, words] of COST_KEYWORDS) {
    if (words.some((w) => t.includes(w))) return COST_DATA.find((c) => c.key === key);
  }
  return null;
}

export default function Assistant() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [mapping, setMapping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const sp = params.get("specialty");
    if (sp) setInput(`I'm interested in ${sp}. Can you guide me?`);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const costCard = detectCost(messages.filter((m) => m.role === "assistant").map((m) => m.content).join(" "));

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("mv_token");
      const res = await fetch(`${API}/v1/ai/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let data;
          try { data = JSON.parse(line); } catch { continue; }
          if (data.type === "session") setSessionId(data.session_id);
          if (data.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.content };
              return copy;
            });
          }
          if (data.type === "error") toast.error("AI error: " + data.content);
        }
      }
    } catch { toast.error("Failed to reach assistant"); } finally { setStreaming(false); }
  };

  const findProviders = async () => {
    const convo = messages.map((m) => `${m.role}: ${m.content}`).join("\n");
    if (!convo) { toast.info("Chat with the assistant first"); return; }
    setMapping(true);
    try {
      const { data } = await api.post("/v1/ai/map", { text: convo });
      const primary = data.specialties?.[0]?.name;
      if (!primary) { toast.error("Could not map your needs"); return; }
      const extra = data.specialties.slice(1).map((s) => s.name);
      toast.success(extra.length ? `Matched to ${primary} + ${extra.join(", ")}` : `Matched to ${primary}`);
      navigate(`/book?specialty=${encodeURIComponent(primary)}`);
    } catch { toast.error("Could not map your needs"); } finally { setMapping(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#8B5CF6] text-white"><MessageSquareHeart size={22} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#101828]">Vaidya · AI Discovery Assistant</h1>
          <p className="text-sm text-muted-foreground">Powered by Claude Sonnet 4.5 · guidance only</p>
        </div>
        {costCard && (
          <Badge className="ml-auto bg-[#D1FAE5] text-[#087443] border-0 hidden sm:inline-flex" data-testid="cost-estimate-badge">
            <TrendingDown size={13} className="mr-1" /> {costCard.label.split("—")[0].trim()}: {money(costCard.india)} in India vs {money(costCard.usa)} USA
          </Badge>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-[#FEF3C7] border border-[#F59E0B]/40 text-[#92400E] p-3 text-sm mb-4" data-testid="ai-disclaimer">
        <ShieldAlert size={18} className="shrink-0 mt-0.5" />
        This AI assistant provides general health information to help you explore treatment options. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Never disregard professional medical advice or delay seeking it because of information provided here.
      </div>

      <div className="rounded-2xl border border-[#E4E7EC] bg-white overflow-hidden">
        <div ref={scrollRef} className="h-[46vh] min-h-[320px] overflow-y-auto p-5 space-y-4" data-testid="chat-window">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Sparkles size={30} className="text-[#8B5CF6] mb-3" />
              <p className="max-w-md text-muted-foreground" data-testid="ai-intro">
                I'm your SarvaSeva care navigator. I'm here to guide you through every step — from finding the right treatment to planning your entire journey to India and back.
              </p>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">Pick a quick start, or describe your condition in your own words.</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg">
                {SCENARIOS.map((s) => (
                  <button key={s.text} onClick={() => send(s.text)} data-testid="quickstart-card"
                    className="flex items-center gap-2.5 text-left rounded-xl border border-[#E4E7EC] bg-[#F7F9FC] px-3.5 py-3 hover:border-[#8B5CF6]/50 hover:bg-[#8B5CF6]/5 transition-colors duration-200">
                    <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#8B5CF6]/10 text-[#8B5CF6] shrink-0"><s.icon size={16} /></span>
                    <span className="text-sm text-[#101828]">{s.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#0D5C75] text-white rounded-br-sm" : "bg-[#8B5CF6]/10 text-[#101828] border border-[#8B5CF6]/20 rounded-bl-sm"
              }`} data-testid={`chat-msg-${m.role}`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="animate-spin" size={16} /> : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E4E7EC] p-3 bg-white">
          <div className="flex items-end gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Describe your condition…" rows={1}
              className="resize-none focus:ring-2 focus:ring-[#8B5CF6]" data-testid="chat-input" />
            <Button onClick={() => send()} disabled={streaming || !input.trim()}
              className="h-10 rounded-full bg-[#8B5CF6] text-white hover:bg-[#7c4ddb] hover:text-white shrink-0 px-4" data-testid="chat-send-btn">
              <Send size={18} />
            </Button>
          </div>
        </div>
      </div>

      {messages.some((m) => m.role === "assistant" && m.content) && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-xl bg-[#E9F5F9] border border-[#0D5C75]/15 p-4">
          <div className="text-sm text-[#0D5C75]">Ready? Let me match you with accredited providers.</div>
          <Button onClick={findProviders} disabled={mapping}
            className="rounded-full bg-[#2A9D8F] text-white hover:bg-[#227d72] hover:text-white" data-testid="view-matched-providers-btn">
            {mapping ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            View Matched Providers <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
